/**
 * Mobile Redirect Component
 * 
 * Handles automatic redirection of mobile users to the mobile experience.
 * This component wraps the main app content and redirects mobile users
 * to mobile-specific routes while preserving desktop functionality.
 */

import React, { useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  shouldRedirectToMobile, 
  getMobileRoute, 
  getDesktopRoute, 
  isMobileRoute 
} from '../../utils/mobileDetection';
import { AuthContext } from '../../components/Auth/AuthContext';

/**
 * Mobile Redirect Component
 * 
 * Automatically redirects mobile users to mobile experience
 * and desktop users to desktop experience.
 * Only runs after authentication is complete to avoid conflicts.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {React.ReactNode} Children or redirect handling
 */
const MobileRedirect = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: [user], loading: [isLoading] } = useContext(AuthContext);
  const isAuthenticated = !!user;

  useEffect(() => {
    // Don't redirect if we're in the middle of an Auth0 callback
    if (location.pathname.includes('callback') || location.search.includes('code=')) {
      return;
    }

    const currentPath = location.pathname;
    const isMobile = shouldRedirectToMobile();
    const isCurrentlyMobileRoute = isMobileRoute(currentPath);

    // If mobile user is on desktop route, redirect to mobile immediately
    // This works even during authentication loading
    if (isMobile && !isCurrentlyMobileRoute) {
      const mobileRoute = getMobileRoute(currentPath);
      navigate(mobileRoute, { replace: true });
      return;
    }

    // If desktop user is on mobile route, redirect to desktop
    // But only if we're not in loading state (to avoid issues during auth)
    if (!isMobile && isCurrentlyMobileRoute && !isLoading) {
      const desktopRoute = getDesktopRoute(currentPath);
      navigate(desktopRoute, { replace: true });
      return;
    }
  }, [location.pathname, navigate, isAuthenticated, isLoading]);

  return children;
};

export default MobileRedirect; 